<template>
  <div>
    <!-- Filters Section -->
    <transition name="fade" mode="out-in">
      <b-card v-if="showFilters" no-body class="mb-2">
        <b-card-header class="d-flex justify-content-between pl-1 pt-1">
          <div class="d-flex align-items-center">
            <h5 class="mb-0">
              {{ $t('Filters') }}
            </h5>
            <b-badge v-if="hasActiveFilters" variant="primary" class="ml-2">
              {{ $t('Active') }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              v-if="hasActiveFilters"
              variant="outline-secondary"
              size="sm"
              @click="clearAllFilters"
              class="mr-2"
              v-b-tooltip.hover
              :title="$t('Clear filters')">
              <feather-icon icon="Trash2Icon" size="14"/>
            </b-button>
            <b-avatar 
              @click="showFilters = false" 
              v-b-tooltip.hover 
              :title="$t('Close Filters')" 
              class="mr-1"
              button 
              variant="danger" 
              size="30px">
              <feather-icon icon="XIcon"/>
            </b-avatar>
          </div>
        </b-card-header>
        
        <b-card-body>
          <b-row class="mb-1">
            <!-- Filter: Device (Auto-suggest) -->
            <b-col cols="12" md="6" class="mb-md-0 mb-2">
              <h5 class="font-weight-bold">{{ $t('Device') }}</h5>
              <vue-autosuggest 
                v-model="searchDevice" 
                :suggestions="filteredDeviceOptions" 
                :input-props="deviceInputProps"
                @selected="selectDeviceHandler" 
                @input="onDeviceInputChange">
                <template slot-scope="{suggestion}">
                  <div class="my-suggestion-item">
                    <div>
                      <span class="font-weight-bold">{{ suggestion.item.modemIdentifier }}</span>
                      <span v-if="suggestion.item.assetIdentifier" class="ml-1 text-primary">
                        ({{ suggestion.item.assetIdentifier }})
                      </span>
                    </div>
                    <div class="text-muted small">
                      <span v-if="suggestion.item.deviceModelName">{{ suggestion.item.deviceModelName }}</span>
                      <span v-if="suggestion.item.simCardNumber" class="ml-1">| SIM: {{ suggestion.item.simCardNumber }}</span>
                      <span v-if="!suggestion.item.deviceModelName && !suggestion.item.simCardNumber">{{ suggestion.item.customerName }}</span>
                    </div>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Filter: Customer (Auto-suggest) -->
            <b-col cols="12" md="6">
              <h5 class="font-weight-bold">{{ $t('Customer') }}</h5>
              <vue-autosuggest 
                v-model="searchCustomer" 
                :suggestions="filteredCustomerOptions" 
                :input-props="customerInputProps"
                @selected="selectCustomerHandler" 
                @input="onCustomerInputChange">
                <template slot-scope="{suggestion}">
                  <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                  <div v-show="suggestion.item.ownerName">
                    <span class="text-muted">{{ $t('Belongs to: ') }}</span>
                    <span class="font-weight-bold">{{ suggestion.item.ownerName }}</span>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>
          </b-row>

          <b-row>
            <!-- Filter: Date Range -->
            <b-col cols="12">
              <h5 class="font-weight-bold">{{ $t('Installation Period') }}</h5>
              <flat-pickr
                v-model="dateRange"
                class="form-control"
                :config="dateRangeConfig"
                :placeholder="$t('Select date range')"
                @on-change="onDateRangeChange"
              />
            </b-col>
          </b-row>

          <!-- Search Button -->
          <b-row class="mt-2">
            <b-col cols="12" class="d-flex justify-content-end">
              <b-button
                variant="primary"
                @click="handleSearch"
                class="mr-1">
                <feather-icon icon="SearchIcon" class="mr-50"/>
                {{ $t('Search') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Main Table Card -->
    <b-card no-body>
      <div class="m-2">
        <!-- Table Top Controls -->
        <b-row>
          <!-- Per Page Selector -->
          <b-col cols="12" md="4" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <!-- Selector removido para fixar em 10 itens -->
          </b-col>

          <!-- Action Buttons -->
          <b-col cols="12" md="8">
            <div class="d-flex align-items-center justify-content-end">
              <!-- Filter Button -->
              <span v-show="!showFilters" class="float-right">
                <b-button
                  class="btn-icon position-relative"
                  @click="showFilters = true"
                  :variant="hasActiveFilters ? 'success' : 'primary'"
                  v-b-tooltip.hover
                  :title="hasActiveFilters ? $t('Open Filters (Active)') : $t('Open Filters')">
                  <feather-icon icon="FilterIcon"/>
                  <b-badge
                    v-if="hasActiveFilters"
                    variant="warning"
                    pill
                    class="position-absolute badge-notification">
                    !
                  </b-badge>
                </b-button>
              </span>

              <!-- Clear Filters Button -->
              <b-button
                v-if="hasActiveFilters"
                class="ml-1"
                variant="outline-secondary"
                style="min-width: 150px; white-space: nowrap;"
                @click="clearAllFilters"
                v-b-tooltip.hover
                :title="$t('Clear Filters')">
                <feather-icon icon="XIcon" size="14"/>
                {{ $t('Clear Filters') }}
              </b-button>

              <!-- Export Button -->
              <b-button
                variant="success"
                class="ml-1"
                @click="exportToExcel"
                v-b-tooltip.hover
                :title="$t('Export to Excel')">
                <feather-icon icon="DownloadIcon" size="16"/>
                {{ $t('Export Excel') }}
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Installations Table -->
      <b-table 
        ref="refDeviceInstallationsTable" 
        :items="fetchInstallations" 
        responsive 
        :fields="tableColumns" 
        primary-key="id"
        show-empty 
        :empty-text="$t('No matching records found')" 
        class="position-relative">

        <!-- Column: Modem Identifier -->
        <template #head(modemIdentifier)>
          {{ $t('Modem Identifier') }}
        </template>
        <template #cell(modemIdentifier)="data">
          <span class="font-weight-bold">{{ data.item.modemIdentifier || '-' }}</span>
        </template>

        <!-- Column: Asset -->
        <template #head(assetIdentifier)>
          {{ $t('Asset') }}
        </template>
        <template #cell(assetIdentifier)="data">
          <div>
            <span class="font-weight-bold text-uppercase">
              {{ data.item.plate || data.item.chassisNumber || '-' }}
            </span>
            <div v-if="data.item.nickName" class="text-muted small">
              {{ data.item.nickName }}
            </div>
          </div>
        </template>

        <!-- Column: Install Date -->
        <template #head(installationDate)>
          {{ $t('Install Date') }}
        </template>
        <template #cell(installationDate)="data">
          <span>{{ data.item.installationDate || '-' }}</span>
        </template>

        <!-- Column: Installation Technician -->
        <template #head(installationTechnician)>
          {{ $t('Installation Technician') }}
        </template>
        <template #cell(installationTechnician)="data">
          <span>{{ data.item.instTechnicianName || '-' }}</span>
        </template>

        <!-- Column: Responsible Installation -->
        <template #head(responsibleInstallation)>
          {{ $t('Responsible Installation') }}
        </template>
        <template #cell(responsibleInstallation)="data">
          <span>{{ data.item.responsibleInstallation || '-' }}</span>
        </template>

        <!-- Column: Uninstall Date -->
        <template #head(deinstallationDate)>
          {{ $t('Uninstall Date') }}
        </template>
        <template #cell(deinstallationDate)="data">
          <span>{{ data.item.deinstallationDate || '-' }}</span>
        </template>

        <!-- Column: Deinstallation Technician -->
        <template #head(deinstallationTechnician)>
          {{ $t('Uninstall Technician') }}
        </template>
        <template #cell(deinstallationTechnician)="data">
          <span>{{ data.item.deinstTechnicianName || '-' }}</span>
        </template>

        <!-- Column: Responsible Uninstallation -->
        <template #head(responsibleUninstallation)>
          {{ $t('Responsible Uninstallation') }}
        </template>
        <template #cell(responsibleUninstallation)="data">
          <span>{{ data.item.responsibleUninstallation || '-' }}</span>
        </template>
      </b-table>

      <!-- Pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              {{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{ dataMeta.to }} {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}
            </span>
          </b-col>
          
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination 
              v-model="currentPage" 
              :total-rows="totalInstallations" 
              :per-page="perPage" 
              first-number 
              last-number
              class="mb-0 mt-1 mt-sm-0" 
              prev-class="prev-item" 
              next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18"/>
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18"/>
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Export Modal -->
    <b-modal 
      ref="modal-export" 
      size="sm" 
      centered 
      hide-header 
      hide-footer 
      no-close-on-esc 
      no-close-on-backdrop
      hide-header-close>
      <div class="mx-3 text-center">
        <h3>{{ $t('Preparing report') }}...</h3>
        <b-spinner variant="primary" :label="$t('Loading')"/>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BTable,
  BAvatar,
  BBadge,
  BPagination,
  BSpinner,
  BFormInput,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, onUnmounted } from '@vue/composition-api'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import store from '@/store'
import deviceStoreModule from './deviceStoreModule'
import useDeviceInstallationsList from './device-installations-list/useDeviceInstallationsList'
import { VueAutosuggest } from 'vue-autosuggest'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BTable,
    BAvatar,
    BBadge,
    BPagination,
    BSpinner,
    BFormInput,
    vSelect,
    VueAutosuggest,
    flatPickr,
  },
  
  data() {
    return {
      dateRange: null,
      dateRangeConfig: {
        mode: 'range',
        dateFormat: 'd/m/Y',
        locale: Portuguese,
        allowInput: true,
      },
      
      filteredDeviceOptions: [],
      deviceInputProps: {
        id: 'device-autosuggest',
        class: 'form-control',
        placeholder: this.$t('Search by modem identifier...'),
      },
      
      filteredCustomerOptions: [],
      customerInputProps: {
        id: 'customer-autosuggest',
        class: 'form-control',
        placeholder: this.$t('Search customer...'),
      },
    }
  },

  computed: {
    hasActiveFilters() {
      return this.deviceId || 
             this.customerId || 
             this.startDate || 
             this.endDate ||
             this.searchDevice ||
             this.searchCustomer
    },
  },

  methods: {
    // Device Auto-suggest handlers
    selectDeviceHandler(option) {
      if (option && option.item) {
        const modemIdentifier = String(option.item.modemIdentifier || '')
        this.updateSearchDevice(modemIdentifier)
        this.updateDeviceId(option.item.modemId)
        this.filteredDeviceOptions = []
      }
    },

    onDeviceInputChange(text) {
      // Garantir que text seja sempre uma string
      const searchText = typeof text === 'string' ? text : String(text || '')
      this.updateSearchDevice(searchText)
      
      if (!searchText || searchText.length < 2) {
        this.updateDeviceId('')
        this.filteredDeviceOptions = []
        return
      }

      store.dispatch('app-device/fetchAllDevices', {
        q: searchText,
        page: 1,
        perPage: 10,
      })
      .then(response => {
        const devices = response.data.data || []
        this.filteredDeviceOptions = devices.length > 0 ? [{
          data: devices,
        }] : []
      })
      .catch(error => {
        this.filteredDeviceOptions = []
      })
    },

    // Customer Auto-suggest handlers
    selectCustomerHandler(option) {
      if (option && option.item) {
        const customerName = String(option.item.name || '')
        this.updateSearchCustomer(customerName)
        this.updateCustomerId(option.item.id)
        this.filteredCustomerOptions = []
      }
    },

    onCustomerInputChange(text) {
      // Garantir que text seja sempre uma string
      const searchText = typeof text === 'string' ? text : String(text || '')
      this.updateSearchCustomer(searchText)
      
      if (!searchText || searchText.length < 3) {
        this.updateCustomerId('')
        this.filteredCustomerOptions = []
        return
      }

      store.dispatch('app-device/fetchCustomers', {
        q: searchText,
      })
      .then(response => {
        const customers = response.data.data || []
        this.filteredCustomerOptions = customers.length > 0 ? [{
          data: customers,
        }] : []
      })
      .catch(error => {
        this.filteredCustomerOptions = []
      })
    },

    // Date range handler
    onDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        const startDate = this.formatDateForAPI(selectedDates[0])
        const endDate = this.formatDateForAPI(selectedDates[1])
        this.updateDateRange(startDate, endDate)
      } else {
        this.updateDateRange('', '')
      }
    },

    formatDateForAPI(date) {
      if (!date) return ''
      const d = new Date(date)
      // Define para início do dia (00:00:00) em UTC para consistência
      d.setHours(0, 0, 0, 0)
      // Retorna no formato ISO 8601 que o .NET 6 entende perfeitamente
      return d.toISOString()
    },

    // Clear all filters
    clearAllFilters() {
      this.clearFilters()
      this.filteredDeviceOptions = []
      this.filteredCustomerOptions = []
      this.dateRange = null
      this.refetchData()
    },

    // Handle search button click
    handleSearch() {
      this.refetchData()
    },

    // Export to Excel
    async exportToExcel() {
      this.$refs['modal-export'].show()
      
      try {
        const response = await store.dispatch('app-device/fetchDeviceInstallationsExcel', {
          deviceId: this.deviceId,
          customerId: this.customerId,
          startDate: this.startDate,
          endDate: this.endDate,
        })
        
        const date = new Date().toLocaleDateString(getIntlLocale()).replace(/\//g, '-')
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `device_installations_${date}.xls`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$refs['modal-export'].hide()
        
        this.$swal({
          title: this.$t('Success'),
          text: this.$t('Report exported successfully'),
          icon: 'success',
          confirmButtonText: this.$t('Close'),
        })
      } catch (error) {
        this.$refs['modal-export'].hide()
        
        this.$swal({
          title: this.$t('Error'),
          text: this.$t('Error exporting report'),
          icon: 'error',
          confirmButtonText: this.$t('Close'),
        })
      }
    },
  },

  setup() {
    const DEVICE_APP_STORE_MODULE_NAME = 'app-device'

    // Register module
    if (!store.hasModule(DEVICE_APP_STORE_MODULE_NAME)) {
      store.registerModule(DEVICE_APP_STORE_MODULE_NAME, deviceStoreModule)
    }

    // Unregister on leave
    onUnmounted(() => {
      if (store.hasModule(DEVICE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(DEVICE_APP_STORE_MODULE_NAME)
      }
    })

    const {
      fetchInstallations,
      tableColumns,
      perPage,
      currentPage,
      totalInstallations,
      dataMeta,
      perPageOptions,
      searchQuery,
      refDeviceInstallationsTable,
      
      showFilters,
      deviceId,
      customerId,
      startDate,
      endDate,
      searchDevice,
      searchCustomer,
      
      refetchData,
      clearFilters,
      updateDeviceId,
      updateCustomerId,
      updateDateRange,
      updateSearchDevice,
      updateSearchCustomer,
    } = useDeviceInstallationsList()

    return {
      fetchInstallations,
      tableColumns,
      perPage,
      currentPage,
      totalInstallations,
      dataMeta,
      perPageOptions,
      searchQuery,
      refDeviceInstallationsTable,
      
      showFilters,
      deviceId,
      customerId,
      startDate,
      endDate,
      searchDevice,
      searchCustomer,
      
      refetchData,
      clearFilters,
      updateDeviceId,
      updateCustomerId,
      updateDateRange,
      updateSearchDevice,
      updateSearchCustomer,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.badge-notification {
  top: -5px;
  right: -5px;
  font-size: 0.6rem;
  min-width: 16px;
  height: 16px;
  line-height: 14px;
}

.my-suggestion-item {
  padding: 0.5rem 0;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>